import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const t = localStorage.getItem("ishukart_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const errMsg = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
  if (typeof d === "string") return d;
  const s = e?.response?.status;
  if (!e?.response || s === 404 || s >= 500) return "The server is starting up or unreachable — please try again in a few seconds.";
  return e?.message || fallback;
};

const get = (u, params) => client.get(u, { params }).then((r) => r.data);
const post = (u, body) => client.post(u, body).then((r) => r.data);
const put = (u, body) => client.put(u, body).then((r) => r.data);
const patch = (u, body) => client.patch(u, body).then((r) => r.data);
const del = (u) => client.delete(u).then((r) => r.data);

export const renderUrl = (versionId, path = "") => `${API}/render/v/${versionId}/${path}`;
export const sharedRenderUrl = (token, access, path = "") => `${API}/render/s/${token}/${access}/${path}`;
export const assetUrl = (u) => (u?.startsWith("/") ? `${BACKEND_URL}${u}` : u);
export const publishedRenderUrl = (slug, path = "") => `${API}/render/p/${slug}/${path}`;

export const api = {
  // auth & profile
  updateProfile: (data) => patch("/auth/me", data),

  // models & projects
  models: () => get("/models"),
  projects: () => get("/projects"),
  createProject: (prompt, model, opts = {}) => post("/projects", { prompt, model, ...opts }),
  duplicateProject: (id, version_id) => post(`/projects/${id}/duplicate`, version_id ? { version_id } : {}),
  projectSettings: (id, body) => patch(`/projects/${id}/settings`, body),
  stopBuild: (id) => post(`/projects/${id}/stop`),
  upload: (file, onProgress) => {
    const form = new FormData();
    form.append("file", file);
    return client.post("/uploads", form, {
      onUploadProgress: (e) => onProgress?.(Math.round((e.loaded / (e.total || 1)) * 100)),
    }).then((r) => r.data);
  },
  project: (id) => get(`/projects/${id}`),
  renameProject: (id, name) => patch(`/projects/${id}`, { name }),
  deleteProject: (id) => del(`/projects/${id}`),
  messages: (id) => get(`/projects/${id}/messages`),
  message: (id, mid) => get(`/projects/${id}/messages/${mid}`),
  sendMessage: (id, text, model, opts = {}) => post(`/projects/${id}/messages`, { text, model, ...opts }),
  versions: (id) => get(`/projects/${id}/versions`),
  version: (id, vid) => get(`/projects/${id}/versions/${vid}`),
  restoreVersion: (id, vid) => post(`/projects/${id}/versions/${vid}/restore`),
  saveFiles: (id, body) => put(`/projects/${id}/files`, body),
  exportZip: (id, versionId) =>
    client.get(`/projects/${id}/export.zip`, { params: versionId ? { version_id: versionId } : {}, responseType: "blob" }),

  // publishing
  checkSlug: (slug, project_id) => get("/publish/check-slug", { slug, project_id }),
  publish: (id, slug) => post(`/projects/${id}/publish`, { slug }),
  unpublish: (id) => post(`/projects/${id}/unpublish`),
  publishedMeta: (slug) => get(`/published/${slug}`),
  setDomain: (id, domain) => post(`/projects/${id}/domain`, { domain }),
  verifyDomain: (id) => post(`/projects/${id}/domain/verify`),
  removeDomain: (id) => del(`/projects/${id}/domain`),
  resolveHost: (host) => get("/domains/resolve", { host }),

  // private share links
  createShare: (id, passcode) => post(`/projects/${id}/share`, { passcode: passcode || null }),
  deleteShare: (id) => del(`/projects/${id}/share`),
  shareMeta: (token) => get(`/share/${token}`),
  shareUnlock: (token, passcode) => post(`/share/${token}/unlock`, { passcode }),

  // app database
  dbSummary: (id) => get(`/projects/${id}/db`),
  dbRecords: (id, coll) => get(`/projects/${id}/db/c/${coll}`),
  dbDeleteRecord: (id, coll, rid) => del(`/projects/${id}/db/c/${coll}/${rid}`),
  dbUsers: (id) => get(`/projects/${id}/db/users`),
  dbDeleteUser: (id, uid) => del(`/projects/${id}/db/users/${uid}`),
  dbAdmin: (id) => get(`/projects/${id}/db/admin`),
  dbAdminReset: (id) => post(`/projects/${id}/db/admin/reset`),

  // github
  githubStatus: () => get("/github/status"),
  githubConnect: (token) => post("/github/token", { token }),
  githubDisconnect: () => del("/github/token"),
  githubPush: (id, repo, isPrivate) => post(`/projects/${id}/github/push`, { repo, private: isPrivate }),

  // credits
  credits: () => get("/credits/me"),
  creditPacks: () => get("/credits/packs"),
  creditHistory: () => get("/credits/history"),
  creditsPricing: () => get("/credits/pricing"),
  creditsCreateOrder: (sel) => post("/credits/create-order", typeof sel === "string" ? { pack_id: sel } : sel),
  creditsVerify: (body) => post("/credits/verify", body),

  // public forms
  submitContact: (data) => post("/contact", data),
  requestDataDeletion: (data) => post("/data-deletion/request", data),

  // video studio
  videoPricing: () => get("/videos/pricing"),
  videoAccess: () => get("/videos/access"),
  videos: (projectId) => get("/videos", projectId ? { project_id: projectId } : undefined),
  createVideo: (body) => post("/videos", body),
  video: (id) => get(`/videos/${id}`),
  updateVideo: (id, body) => put(`/videos/${id}`, body),
  deleteVideo: (id) => client.delete(`/videos/${id}`).then((r) => r.data),
  addClip: (id, body) => post(`/videos/${id}/clips`, body),
  makeMusic: (id, prompt) => post(`/videos/${id}/music`, { prompt }),
  exportVideo: (id) => post(`/videos/${id}/export`),

  // push notifications
  pushConfig: () => get("/push/config"),
  pushSubscribe: (sub) => post("/push/subscribe", sub),
  pushUnsubscribe: (endpoint, token) =>
    client.post("/push/unsubscribe", { endpoint }, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined).then((r) => r.data),
  pushStatus: () => get("/push/status"),
  pushTest: () => post("/push/test"),

  // mobile app
  appEvent: (body) => post("/mobile-app/event", body),
  adminMobileStats: () => get("/admin/mobile-app/stats"),
  mobileApp: () => get("/mobile-app"),
  adminMobileApp: () => get("/admin/mobile-app"),
  adminSaveMobileApp: (body) => put("/admin/mobile-app", body),
  adminMobileKit: (key) => client.get(`/admin/mobile-app/kit/${key}`, { responseType: "blob" }),

  // admin
  adminBootstrapStatus: () => get("/admin/bootstrap-status"),
  adminBootstrap: () => post("/admin/bootstrap"),
  adminStats: () => get("/admin/stats"),
  adminUsers: (params) => get("/admin/users", params),
  adminUserDetail: (id) => get(`/admin/users/${id}`),
  adminBlockUser: (id, reason) => patch(`/admin/users/${id}/block`, { reason }),
  adminUnblockUser: (id) => patch(`/admin/users/${id}/unblock`),
  adminPromote: (id) => patch(`/admin/users/${id}/promote`),
  adminDemote: (id) => patch(`/admin/users/${id}/demote`),
  adminDeleteUser: (id) => del(`/admin/users/${id}`),
  adminGrantCredits: (id, amount, note) => post(`/admin/users/${id}/credits`, { amount, note }),
  adminAddCredit: (id, body) => post(`/admin/users/${id}/credits`, body),
  adminCreditsOverview: () => get("/admin/credits/overview"),
  adminCreditsStatus: () => get("/admin/credits/status"),
  adminPanel: () => get("/admin/panel"),
  adminPanelCreateOrder: (sel) => post("/admin/panel/create-order", sel),
  adminPanelVerify: (body) => post("/admin/panel/verify", body),
  adminPaymentLinks: () => get("/admin/payment-links"),
  adminPaymentLink: (id) => get(`/admin/payment-links/${id}`),
  adminCreatePaymentLink: (body) => post("/admin/payment-links", body),
  adminCancelPaymentLink: (id) => post(`/admin/payment-links/${id}/cancel`, {}),
  adminSaveAlerts: (body) => put("/admin/credits/alert-settings", body),
  adminAddTopup: (body) => post("/admin/emergent-topups", body),
  adminDeleteTopup: (id) => del(`/admin/emergent-topups/${id}`),
  adminProjects: (params) => get("/admin/projects", params),
  adminCreditOrders: () => get("/admin/credit-orders"),
  adminPricing: () => get("/admin/pricing"),
  adminSavePricing: (body) => client.put("/admin/pricing", body).then((r) => r.data),
  adminEarnings: () => get("/admin/earnings"),
  adminDomains: () => get("/admin/domains"),
  adminDomainEarnings: () => get("/admin/domains/earnings"),
  adminDomainLive: (id, live) => post(`/admin/domains/${id}/live`, { live }),
  domainRenewal: () => get("/domains/renewal"),
  adminContact: () => get("/admin/contact"),
  adminMarkContactRead: (id) => post(`/admin/contact/${id}/read`),
  adminDeleteContact: (id) => del(`/admin/contact/${id}`),
  adminAuditLog: (params) => get("/admin/audit-log", params),
  adminAuditLogExportCsv: (params) => client.get("/admin/audit-log/export.csv", { params, responseType: "blob" }),
};

export const downloadBlob = (res, fallbackName) => {
  const cd = res.headers?.["content-disposition"] || "";
  const name = (cd.match(/filename="?([^"]+)"?/) || [])[1] || fallbackName;
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  return name;
};
